import qs from "query-string";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

// import useParentLoaderData from "../../../../../hooks/useParentLoaderData";
import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import { mapProviderContractData, transformProviderDetailData } from "../helper/functions";
import { useProviderDetailStore } from "../store/provider-details/providerDetailStore";

export default function useFetchProviderDetails() {
  const providerRepository = useService("repositories/provider-repository");
  const enumRepository = useService("repositories/enum-repository");

  const params = useParams();
  const id = params?.id;
  const {
    setData,
    setLoading,
    setAdditionalEmailTypes,
    setAdditionalPhoneTypes,
    setCommunicationPreferenceTypes,
    setActiveStates,
    setInitialData,
    setModifiedData,
    setProviderTypesRange,
  } = useProviderDetailStore();
  const { openFailureModal } = getModalHandler();

  const fetchProviderDetail = useCallback(async () => {
    const response = await providerRepository.findRecord(id, { noCache: true });
    const transformed = transformProviderDetailData(response);

    let mergedData = transformed;
    if (transformed?.entity) {
      try {
        const contractResponse = await providerRepository.getContract(id);
        mergedData = {
          ...transformed,
          ...mapProviderContractData(contractResponse),
        };
      } catch (error) {
        console.error(error);
        mergedData = {
          ...transformed,
          contractEntity: "",
          ein: "",
          contractStartDate: "",
          contractEndDate: "",
        };
      }
    }

    setData(mergedData);
    setInitialData({ ...mergedData });
    setModifiedData({ ...mergedData });
  }, [id, providerRepository, setData, setInitialData, setModifiedData]);

  const fetchProviderAdditionalEmailTypes = useCallback(async () => {
    const queryParams = {
      content_type: "provideremail",
      field_name: "email_type",
      flat: "True",
    };

    const url = `field_options/?${qs.stringify(queryParams)}`;
    const response = await enumRepository.query({ urlPath: url });
    const orderedEmailTypeMap = new Map(
      response
        ?.slice()
        .sort((a, b) => Number(a.key) - Number(b.key))
        .map((item) => [Number(item.key), item])
    );

    setAdditionalEmailTypes(orderedEmailTypeMap);
  }, [enumRepository, setAdditionalEmailTypes]);

  const fetchProviderAdditionalPhoneTypes = useCallback(async () => {
    const queryParams = {
      content_type: "providercontactinfo",
      field_name: "contact_label",
      flat: "True",
    };

    const url = `field_options/?${qs.stringify(queryParams)}`;
    const response = await enumRepository.query({ urlPath: url });
    const orderedPhoneTypeMap = new Map(
      response
        ?.slice()
        .sort((a, b) => Number(a.key) - Number(b.key))
        .map((item) => [Number(item.key), item])
    );
    setAdditionalPhoneTypes(orderedPhoneTypeMap);
  }, [enumRepository, setAdditionalPhoneTypes]);

  const fetchProviderCommunicationPreferenceTypes = useCallback(async () => {
    const queryParams = {
      content_type: "provider",
      field_name: "communication_preference",
      flat: "True",
    };

    const url = `field_options/?${qs.stringify(queryParams)}`;
    const response = await enumRepository.query({ urlPath: url });
    setCommunicationPreferenceTypes(response);
  }, [enumRepository, setCommunicationPreferenceTypes]);

  const fetchActiveStates = useCallback(async () => {
    const response = await enumRepository.query({ urlPath: "core/dynamic-options/states" });
    const formatted = (response ?? []).map((item) => ({
      id: item?.key ?? item?.id ?? item?.value,
      label: item?.label ?? item?.name ?? item?.value ?? "",
    }));
    setActiveStates(formatted);
  }, [enumRepository, setActiveStates]);

  const fetchProviderTypeRange = useCallback(async () => {
    const url = `provider/validate/`;
    const response = await providerRepository.query({ urlPath: url });
    if (response) {
      const min = Number(response?.providerType.min) || 0;
      const max =
        response?.providerType.max !== null
          ? Number(response?.providerType.max)
          : Number.MAX_SAFE_INTEGER;
      setProviderTypesRange(min, max);
    }
  }, [enumRepository, setProviderTypesRange]);

  const fetchProviderDetails = useCallback(async () => {
    try {
      setLoading(true);

      await Promise.all([
        fetchProviderDetail(),
        fetchProviderAdditionalEmailTypes(),
        fetchProviderAdditionalPhoneTypes(),
        fetchProviderCommunicationPreferenceTypes(),
        fetchActiveStates(),
        fetchProviderTypeRange(),
      ]);
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    } finally {
      setLoading(false);
    }
  }, [
    fetchProviderDetail,
    fetchProviderAdditionalEmailTypes,
    fetchProviderAdditionalPhoneTypes,
    fetchProviderCommunicationPreferenceTypes,
    fetchActiveStates,
  ]);

  return {
    fetchProviderDetails,
  };
}
